import React, { useMemo } from "react";
import moment from "moment-timezone";
import { useNavigate } from "react-router-dom";
import { CommonDestinationTimezone } from "@/lib/utils";
import { useListDestinationPacketTemplates, type DestinationPacketDocument } from "@/lib/api/destinationPackets";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";
import { RoutePath } from "@/section/router";

export interface DestinationPacketTemplateRow extends Omit<DestinationPacketDocument, "created_at"> {
  created_at: string;
}

function formatDocumentVariant(variant?: string): string {
  if (!variant) return "";
  
  return variant
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function TemplateListSection() {
  const listResult = useListDestinationPacketTemplates();
  const navigate = useNavigate();

  const list_data = useMemo(() => {
    return listResult?.data?.data?.data || [];
  }, [listResult]);

  const columns = useMemo((): DataTableColumn<DestinationPacketTemplateRow>[] => [
    {
      id: "destination_template_packet_id",
      accessorKey: "destination_template_packet_id",
      header: "Packet Name",
      accessorFn: (row) => row.DestinationTemplatePacket?.name,
    },
    {
      id: "template_name",
      accessorKey: "template_name",
      header: "Template",
      cell: ({ row }) => {
        return row.original.DocumentTemplate?.title || formatDocumentVariant(row.original.document_variant);
      },
    },
    {
      id: "document_variant",
      accessorKey: "document_variant",
      header: "Template Type",
      cell: ({ row }) => {
        return formatDocumentVariant(row.original.document_variant);
      },
    },
    {
      id: "created_at",
      accessorKey: "created_at",
      header: "Created",
      cell: (info) => moment.tz(info.getValue(), CommonDestinationTimezone).format('YYYY-MM-DD HH:mm'),
      enableFiltering: false,
    },
  ], []);

  const isLoading = listResult.isLoading;
  const handleOnRowClick = (row: DestinationPacketTemplateRow) => {
    const path = RoutePath.DestinationPacketsTemplatesView.replace(":template_id", row.id);
    navigate(path);
  };

  return (
    <div className="flex flex-col gap-2">
      <DataTable
        stickyHeaders
        tableClassName="h-[calc(50vh-200px)] overflow-y-scroll"
        data={list_data}
        columns={columns}
        enableSorting
        enableMultiSort
        enableFiltering
        enableGrouping
        enableColumnVisibility
        filterStyle="select"
        filterMode="internal"
        defaultSorting={[{ id: "created_at", desc: true }]}
        emptyMessage={isLoading ? "Loading ..." : "No templates found"}
        groupBy={["destination_template_packet_id"]}
        rowClickable
        onRowClick={handleOnRowClick}
      />
    </div>
  );
}

